import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import DashboardRenderer from './dashboard-renderer.vue';
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';
export default {
  name: 'Dashboard',
  components: {
    DashboardRenderer
  },
  props: {
    parameters: {
      type: Object,
      required: false,
      default: () => ({})
    },
    dashboardId: {
      type: String,
      required: true
    },
    widgetMinSize: {
      type: Number,
      required: false,
      default: null
    },
    widgetMinSizeFullscreen: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      editedEditMode: false
    };
  },
  computed: {
    ...mapState('dashboards', ['dashboards', 'status', 'error']),
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'permissions', 'UiTheme']),
    dashboardIsFullscreen: {
      get() {
        return this.$route.query.fullscreen === 'true';
      },
      set(value) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            fullscreen: value ? 'true' : 'false'
          }
        });
      }
    },
    actualWidgetMinSize() {
      let minSize = 0;
      if (this.dashboardIsFullscreen && this.widgetMinSizeFullscreen) {
        minSize = this.widgetMinSizeFullscreen;
      }
      if (!this.dashboardIsFullscreen && this.widgetMinSize) {
        minSize = this.widgetMinSize;
      }
      return minSize;
    },
    activeDashboardLayer() {
      return this.dashboardLayers[this.dashboardLayers.length - 1];
    },
    activeDashboardId() {
      if (!this.activeDashboardLayer) {
        return null;
      }
      return this.activeDashboardLayer.dashboardId;
    },
    activeDashboard() {
      return this.dashboards.find(dashboard => dashboard.id === this.activeDashboardId);
    },
    customParameters() {
      if (this.activeDashboardLayer) {
        return this.activeDashboardLayer.customParameters;
      }
      const dashboard = this.dashboards.find(dashboard => dashboard.id === this.dashboardId);
      const dashboardParams = dashboard ? dashboard.value.customParameters : {};
      return {
        ...dashboardParams,
        ...this.parameters
      };
    },
    modifiedDateRange: {
      get() {
        const startString = this.$route.query['date-range-start'];
        const endString = this.$route.query['date-range-end'];
        if (!startString || !endString) {
          return null;
        }
        const start = new Date(startString);
        const end = new Date(endString);
        return {
          start,
          end
        };
      },
      set(newDateRange) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            'date-range-start': newDateRange.start.toISOString(),
            'date-range-end': newDateRange.end.toISOString()
          }
        });
      }
    },
    actualDateRange: {
      get() {
        if (this.modifiedDateRange) {
          return this.modifiedDateRange;
        }
        const defaultDateRange = get(this.activeDashboard, 'value.defaultDateRange');
        if (defaultDateRange) {
          let start = new Date(this.activeDashboard.value.defaultDateRange.start);
          let end = new Date(this.activeDashboard.value.defaultDateRange.end);
          if (this.activeDashboard.value.defaultDateRange.isRelative) {
            start = new Date(Date.now() - start);
            end = new Date(Date.now() - end);
          }
          return {
            start,
            end
          };
        }
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const now = new Date();
        return {
          start: sevenDaysAgo,
          end: now
        };
      },
      set(newDateRange) {
        this.modifiedDateRange = newDateRange;
      }
    },
    dashboard() {
      return this.dashboards.find(dashboard => dashboard.id === this.dashboardId);
    },
    hasEditPermissions() {
      return this.permissions['Dashboards.manage'];
    },
    editMode: {
      get() {
        if (!this.hasEditPermissions) {
          return false;
        }
        return this.editedEditMode;
      },
      set(newVal) {
        this.editedEditMode = newVal;
      }
    },
    dashboardLayers: {
      get() {
        const defaultLayers = [{
          dashboardId: this.dashboardId,
          customParameters: get(this.dashboard, 'value.customParameters', {}),
          wasReplaced: false
        }];
        // get from "dashboard-layers" query param (json stringified value)
        const dashboardLayersStringFromQuery = this.$route.query['dashboard-layers'];
        if (!dashboardLayersStringFromQuery) {
          return defaultLayers;
        }
        const decompressedDashboardLayers = decompressFromEncodedURIComponent(dashboardLayersStringFromQuery);
        return JSON.parse(decompressedDashboardLayers);
      },
      set(newDashboardLayers) {
        const stringifiedDashboardLayers = JSON.stringify(newDashboardLayers);
        const compressedDashboardLayers = compressToEncodedURIComponent(stringifiedDashboardLayers);
        this.$router.replace({
          query: {
            ...this.$route.query,
            'dashboard-layers': compressedDashboardLayers
          }
        });
      }
    }
  },
  watch: {
    editMode(newEditMode) {
      const newEditModeString = newEditMode ? 'true' : 'false';
      // update query params "edit" to match editMode if its not the same as the current one
      if (newEditModeString !== this.$route.query.edit) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            edit: newEditModeString
          }
        });
      }
      if (!newEditMode) {
        return;
      }
      if (this.activeDashboard) {
        this.nextDashboardName = this.activeDashboard.meta.name;
        this.dashboardNameSaveError = null;
      }
    },
    activeDashboardId: {
      handler(val) {
        if (!val) {
          return;
        }
        this.fetchDashboard();
      },
      immediate: true
    },
    // watch for query params "edit" and change editMode accordingly
    '$route.query.edit': {
      handler(newEditString) {
        const newEdit = newEditString === 'true';
        if (newEdit !== this.editMode) {
          this.editMode = newEdit;
        }
      },
      immediate: true
    }
  },
  methods: {
    async persistsNewWidgetOrder(newOrder) {
      const {
        success,
        error
      } = await this.$store.dispatch('dashboards/updateWidgetOrder', {
        dashboardId: this.activeDashboardId,
        widgetOrder: newOrder
      });
      if (!success) {
        console.error(error);
        this.$snack(this.$t('Dashboards.view.error.savingWidgetOrder'), 'error');
      }
    },
    async fetchDashboard() {
      const {
        success,
        error
      } = await this.$store.dispatch('dashboards/refreshOne', {
        dashboardId: this.activeDashboardId
      });
      if (!success) {
        console.error('error', error);
        this.$snack(this.$t('Dashboards.view.error.refreshingDashboard'), 'error');
      }
    },
    activateSubDashboard({
      dashboardId,
      customParameters,
      navigationType
    }) {
      switch (navigationType) {
        case 'replace':
          {
            const clone = cloneDeep(this.dashboardLayers);
            clone[clone.length - 1] = {
              dashboardId,
              customParameters
            };
            this.dashboardLayers = clone;
            break;
          }
        case 'navigate':
        default:
          {
            const clone = cloneDeep(this.dashboardLayers);
            clone.push({
              dashboardId,
              customParameters
            });
            this.dashboardLayers = clone;
            break;
          }
      }
    },
    goToDashboardLayer({
      layerIndex
    }) {
      const clone = cloneDeep(this.dashboardLayers);
      clone.splice(layerIndex + 1);
      this.dashboardLayers = clone;
    }
  }
};